import { Dispatch, MutableRefObject, SetStateAction, useEffect } from 'react';
import { ChatMessage } from '../shared/interfaces';
import { chooseBackgroundHistory } from './backgroundHistory';

export const BACKGROUND_HISTORY_ERROR = 'Não foi possível carregar a resposta final. Reabra esta conversa para tentar novamente.';
/** Bounded retry of the single final history request (a network blip here
 *  would otherwise leave the answer without its file/tool metadata). */
export const HISTORY_RETRY_DELAYS_MS = [1000, 3000];

const wait = (ms: number, signal: AbortSignal) => new Promise<void>((resolve) => {
  const done = () => { clearTimeout(timer); signal.removeEventListener('abort', done); resolve(); };
  const timer = setTimeout(done, ms);
  signal.addEventListener('abort', done);
});

interface BackgroundRefreshOptions {
  apiBase: string;
  chatId: string;
  streamedText: string;
  signal: AbortSignal;
  isCurrentChat: () => boolean;
  getAccessToken: () => Promise<string>;
  setMessageHistory: Dispatch<SetStateAction<ChatMessage[]>>;
  setError: Dispatch<SetStateAction<string>>;
  onSettled: () => void;
  retryDelaysMs?: number[];
}

/** The terminal frame still needs Mongo's file/tool metadata before teardown. */
export async function refreshCompletedBackgroundChat({
  apiBase, chatId, streamedText, signal, isCurrentChat, getAccessToken,
  setMessageHistory, setError, onSettled, retryDelaysMs = HISTORY_RETRY_DELAYS_MS,
}: BackgroundRefreshOptions): Promise<void> {
  const isCurrent = () => !signal.aborted && isCurrentChat();
  const fetchHistory = async (): Promise<any> => {
    const token = await getAccessToken();
    if (!isCurrent()) return null;
    const response = await fetch(`${apiBase}/api/chat/history/${chatId}`, {
      headers: { authorization: `Auth ${token}` }, signal,
    });
    if (!response.ok) throw new Error(`History refresh failed: HTTP ${response.status}`);
    return response.json();
  };
  try {
    let data: any = null;
    for (let attempt = 0; ; attempt++) {
      try {
        data = await fetchHistory();
        break;
      } catch (error) {
        if (!isCurrent() || attempt >= retryDelaysMs.length) throw error;
        await wait(retryDelaysMs[attempt], signal);
      }
    }
    if (!isCurrent() || data == null) return;
    if (Array.isArray(data.messages) && data.messages.length > 0) {
      const messages = data.messages.map((message: any) => message.files == null ? message : {
        ...message, filenames: message.files.map((file: any) => file.filename),
      });
      setMessageHistory(previous => isCurrent()
        ? chooseBackgroundHistory(previous, messages, streamedText)
        : previous);
    }
  } catch {
    // The run's own error frame (api_sse_error / error) is the actionable
    // cause; never replace it with the generic history message.
    if (isCurrent()) setError(previous => previous || BACKGROUND_HISTORY_ERROR);
  } finally {
    // A cancelled refresh may belong to an earlier visit to this SAME chat.
    // It must not remove membership and abort the replacement resume effect.
    if (isCurrent()) onSettled();
  }
}

interface BackgroundPollingOptions {
  apiBase: string;
  chatIds: Set<string>;
  activeChatIdRef: MutableRefObject<string>;
  getAccessToken: () => Promise<string>;
  setBackgroundChatIds: Dispatch<SetStateAction<Set<string>>>;
  setCompletedChatIds: Dispatch<SetStateAction<Set<string>>>;
}

/** A background-status request that never answers (proxy holding the
 *  connection) must not block the polling forever: the effect no longer
 *  restarts on navigation, so the request itself has to give up. */
export const POLL_REQUEST_TIMEOUT_MS = 10_000;

/** Poll only hidden chats; the active chat owns its stream and final refresh. */
export function useBackgroundChatPolling({
  apiBase, chatIds, activeChatIdRef, getAccessToken,
  setBackgroundChatIds, setCompletedChatIds,
}: BackgroundPollingOptions): void {
  useEffect(() => {
    if (chatIds.size === 0) return;
    const abort = new AbortController();
    // Per chat, so one slow or hung request never blocks the others' badges.
    const inFlight = new Set<string>();
    const canSettle = (chatId: string) => !abort.signal.aborted && activeChatIdRef.current !== chatId;

    const poll = async (chatId: string, token: string) => {
      const request = new AbortController();
      const cancel = () => request.abort();
      abort.signal.addEventListener('abort', cancel);
      const timer = setTimeout(cancel, POLL_REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(`${apiBase}/api/chat/background-status/${chatId}`, {
          headers: { authorization: `Auth ${token}` }, signal: request.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        // Navigation can happen during either await; the ref is read after
        // each one, so the effect does not need to restart on navigation.
        if (!canSettle(chatId)) return;
        if (data.status === 'done' || data.status === 'cancelled' || data.status === 'error') {
          setBackgroundChatIds(previous => {
            if (!canSettle(chatId) || !previous.has(chatId)) return previous;
            const next = new Set(previous);
            next.delete(chatId);
            return next;
          });
          setCompletedChatIds(previous => canSettle(chatId) ? new Set(previous).add(chatId) : previous);
        }
      } finally {
        clearTimeout(timer);
        abort.signal.removeEventListener('abort', cancel);
        inFlight.delete(chatId);
      }
    };

    const interval = setInterval(async () => {
      const due = Array.from(chatIds).filter((chatId) => !inFlight.has(chatId) && canSettle(chatId));
      if (due.length === 0) return;
      due.forEach((chatId) => inFlight.add(chatId));
      let token: string;
      try {
        token = await getAccessToken();
      } catch {
        // Token errors are retried by the next poll.
        due.forEach((chatId) => inFlight.delete(chatId));
        return;
      }
      if (abort.signal.aborted) {
        due.forEach((chatId) => inFlight.delete(chatId));
        return;
      }
      // Transport errors and timeouts are retried by the next poll.
      void Promise.allSettled(due.map((chatId) => poll(chatId, token)));
    }, 3000);

    return () => {
      abort.abort();
      clearInterval(interval);
    };
  }, [apiBase, chatIds, activeChatIdRef, getAccessToken, setBackgroundChatIds, setCompletedChatIds]);
}
